import React, { useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useRouter from '../../../utils/useRouter';
import MasterLayout from '../../../components/AppLayout/MasterLayout';
import { Box } from '@mui/material';
import {
  palette,
  spaces,
} from '../../../assets/styles/themes/tokens';
import { SearchPageContainer } from '../../SearchPage/SearchPageContainer';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { SearchFiltersContainer } from '../../../components/Search/SearchFiltersContainer';
import { SearchFilters, SearchResultsTab } from '../../../types/search';
import queryToSearchFilters from '../../../utils/queryToSearchFilters';
import SearchResults, { SearchResultsItemsTab } from '../../../components/Search/SearchResults/SearchResults';
import {
  searchCourseMindItems as searchCourseMindItemsAction,
  SearchOptions,
} from '../../../actions/search.actions';
import useAction from '../../../utils/useAction';
import {
  getPaginationSelector,
  selectCourseSearchFilters,
} from '../../../reducers/search.selectors';
import services from '../../../services';
import { trackingEvents } from '../../../services/tracking/trackConfig';
import _ from 'lodash';
import useScreenSize from '../../../utils/useScreenSize';
import { useNewNavigation } from '../../../utils/useNewNavigation';
import useCardDimensions from '../../../utils/useCardDimensions';
import { MasterLayoutContext } from '../../../state/MasterLayoutProvider';
import { SearchEventData } from '../../../utils/ampli';

export const useNewUiSearchPagePadding = () => {
  const showNewNavigation = useNewNavigation();

  const { isSmallScreen, isMediumScreen } = useScreenSize();

  if (isSmallScreen || showNewNavigation) return spaces.md;
  if (isMediumScreen) return spaces.lg;
  return spaces.xl;
};

const CourseSearchPage = () => {
  const { t } = useTranslation();
  const { match } = useRouter();
  const cardDimensions = useCardDimensions();
  const { mainContentContainer } = useContext(MasterLayoutContext);

  const requestFilters = useRef<SearchFilters>(queryToSearchFilters(match.location.search));
  const searchCourseMindItems = useAction(searchCourseMindItemsAction);
  const courseMindItemsPagination = useSelector(getPaginationSelector('courseMindItems'));
  const lastCourseSearchFilters = useSelector(selectCourseSearchFilters);

  const haveSearchFiltersChanged = (filters?: SearchFilters) => {
    return !_.isEqual(filters, requestFilters.current);
  };

  const executeCourseMindItemsSearch = (options: SearchOptions) => {
    searchCourseMindItems(requestFilters.current, { ...options, populateProducts: true });
    services.track.eventTrack(trackingEvents.searchExecute,_.merge(eventPayload));
  };

  useEffect(() => {
    requestFilters.current = queryToSearchFilters(match.location.search);
    const options: SearchOptions = {
      clearPreviousResults: true,
    };

    if (haveSearchFiltersChanged(lastCourseSearchFilters)) {
      mainContentContainer?.scrollTo(0, 0);
      executeCourseMindItemsSearch(options);
    }
  }, [
    match.location.search,
    mainContentContainer
  ]);

  const eventPayload: { other: SearchEventData } = {
    other: {
      tab: SearchResultsTab.COURSES,
      results: courseMindItemsPagination.offset,
      query: match.location.query,
      term: requestFilters.current.term,
      appSection: 'courses',
    },
  };

  const tabs: SearchResultsItemsTab[] = [
    {
      key: SearchResultsTab.COURSES,
      name: t('Courses'),
      icon: 'book',
      loadMore: () => executeCourseMindItemsSearch({ clearPreviousResults: false })
    },
  ];

  const containerSx = {
    maxWidth: cardDimensions.desktop.maxWidth,
    marginX: 'auto',
    paddingX: useNewUiSearchPagePadding(),
    width: '100%',
  };

  return <MasterLayout forceNewNavigation>
    <SearchPageContainer forceNewDesign>
      <Box sx={containerSx}>
        <PageHeader
          sx={{ color: palette.text.primary }}
          text={t('Find your course')}
        />
        <SearchFiltersContainer
          requestFilters={requestFilters.current}
          visibleKeys={['courseTheme', 'schoolType', 'courseType', 'publisher']}
          invisibleKeys={['courseDuration', 'subject', 'classYear']}
          dialogFiltersType='course'
          dialogFiltersOrder={[
            'courseTheme',
            'schoolType',
            'courseType',
            'publisher',
            'courseDuration',
            'subject',
            'classYear'
          ]}
        />
      </Box>
      <Box marginTop='-24px'>
        <SearchResults
          trackingPayload={eventPayload.other}
          requestFilters={requestFilters.current}
          tabs={tabs}
          currentTab={SearchResultsTab.COURSES}
          searchTerm={requestFilters.current.term}
          forceNewDesign
        />
      </Box>
    </SearchPageContainer>
  </MasterLayout>;
};

export default CourseSearchPage;
